import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";

const SeoPage = ({ page }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    window.__lenis?.scrollTo(0, { immediate: true });
  }, [page.slug]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: page.h1,
        description: page.meta,
        provider: { "@type": "LocalBusiness", name: "Fumiga" },
        areaServed: page.area,
      },
      page.faq?.length
        ? {
            "@type": "FAQPage",
            mainEntity: page.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }
        : null,
    ].filter(Boolean),
  };

  return (
    <>
      <Seo
        title={page.title}
        siteName="Fumiga"
        description={page.meta}
        image="/fotos/compartir-og-1200x630.webp"
        jsonLd={jsonLd}
      />
      <section className="relative bg-[#151A1F] pt-36 sm:pt-44 pb-20 sm:pb-24 overflow-hidden">
        <div className="absolute inset-0 hairlines" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#00A651]"
          >
            {page.eyebrow}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-bold tracking-tight leading-[1.06] text-3xl sm:text-5xl lg:text-6xl mt-6 max-w-4xl"
          >
            {page.h1}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 max-w-2xl text-base sm:text-lg text-[#F6F7F5]/70 leading-relaxed"
          >
            {page.intro}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-10"
          >
            <Link
              data-testid="seo-page-cta-cotizar"
              to="/cotizar"
              className="inline-block bg-[#FFB000] text-[#151A1F] font-display font-semibold px-8 py-4 rounded-full text-base hover:scale-[1.03] transition-transform duration-300"
            >
              Cotizar ahora
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="bg-[#F6F7F5] text-[#151A1F] py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#151A1F]/10 border border-[#151A1F]/10">
            {page.blocks.map((b, i) => (
              <motion.article
                key={b.heading}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="bg-[#F6F7F5] p-8 sm:p-12"
                data-testid={`seo-block-${i}`}
              >
                <h2 className="font-display font-bold text-xl sm:text-2xl tracking-tight">{b.heading}</h2>
                <p className="mt-4 text-base text-[#151A1F]/70 leading-relaxed">{b.body}</p>
                {b.bullets && (
                  <ul className="mt-6 space-y-3">
                    {b.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3 text-base text-[#151A1F]/80">
                        <span className="mt-2 w-2 h-2 shrink-0 bg-[#00A651]" aria-hidden="true" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}
              </motion.article>
            ))}
          </div>

          {page.faq?.length > 0 && (
            <div className="mt-16 max-w-3xl">
              <h2 className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#046A38]">
                Preguntas de campo
              </h2>
              <div className="mt-6 border-t border-[#151A1F]/10">
                {page.faq.map((f, i) => (
                  <details key={f.q} className="group border-b border-[#151A1F]/10 py-5" data-testid={`faq-item-${i}`}>
                    <summary className="cursor-pointer font-display font-semibold text-lg list-none flex justify-between items-center gap-4">
                      {f.q}
                      <span className="text-[#046A38] text-2xl font-normal group-open:rotate-45 transition-transform duration-300">+</span>
                    </summary>
                    <p className="mt-3 text-base text-[#151A1F]/70 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <CtaBand wa={page.wa} />
    </>
  );
};

export default SeoPage;
